import { useQuery } from '@tanstack/react-query'
import kebabCase from 'just-kebab-case'
import { useEffect, useMemo } from 'react'
import {
	type LoaderFunctionArgs,
	unstable_useRoute,
	useLoaderData,
	useParams,
} from 'react-router'
import { IconSetErrorBoundary } from '#app/components/icon-set-error-boundary.js'
import { IconSetPage } from '#app/components/icon-set-page.js'
import { trpc } from '#app/lib/trpc-tanstack.tsx'
import { getUserId } from '#app/utils/auth.server.js'
import { getSsrDirectories } from '#app/utils/get-ssr-directories.js'
import {
	filterIconsBySubPath,
	iconsToDirectories,
} from '#app/utils/group-icons-by-directory.js'
import {
	formatCount,
	getSsrHasMoreIcons,
} from '#app/utils/misc.js'
import { loadSearchParams } from '#app/utils/search-params.js'
import { useSerialQueries } from '#app/utils/serial-query.js'
import { useFreeSmartSearchLimit } from '#app/utils/use-free-smart-search-limit.js'
import { useIconSetSearch } from '#app/utils/use-icon-set-search.js'
import { useIconSetSmartSearch } from '#app/utils/use-icon-set-smart-search.js'
import { useOptionalUser } from '#app/utils/user.js'

export async function loader({ request, params }: LoaderFunctionArgs) {
	const userId = await getUserId(request)
	if (userId) return null // skip SSR for logged-in users

	const url = new URL(request.url)
	if (url.searchParams.size > 0) return null // skip SSR if any search params

	const path = params['*']
	const slug = path?.split('/').at(0)
	if (!slug) return null // "all icons" route - skip SSR

	// query/grouping will be defaults since we returned early for requests with search params
	const { query, grouping } = loadSearchParams(request)
	const kebabSlug = kebabCase(slug)
	const kebabPath = path ? path.split('/').map(kebabCase).join('/') : ''

	const ssrDirectories = await getSsrDirectories({
		request,
		path: kebabPath,
		iconSetSlug: kebabSlug,
		query,
		grouping,
	})

	return { ssrDirectories, slug: kebabSlug }
}

export function clientLoader() {
	return null
}

export default function PublicIconSetPage() {
	const params = useParams()
	const loaderData = useLoaderData<typeof loader | typeof clientLoader>()
	const user = useOptionalUser()
	const isLoggedIn = Boolean(user)

	const rootData = unstable_useRoute('root')?.loaderData

	// Get iconSetsInfo from tRPC query (already prefetched in layout)
	const { data: publicTreeData } = useQuery(
		trpc.iconSets.getPublicTree.queryOptions(),
	)
	const iconSetsInfo = useMemo(
		() => publicTreeData?.iconSetsInfo ?? [],
		[publicTreeData],
	)

	const path = params['*']
	// Kebab-case path and slug from URL for consistency with tRPC queries
	const kebabPath = path ? path.split('/').map(kebabCase).join('/') : undefined
	const slug = path?.split('/').at(0)
	const kebabSlug = slug ? kebabCase(slug) : undefined

	// Look up icon set info for path filtering (directory paths use slug, not ID)
	const iconSetInfoForPath = kebabSlug
		? iconSetsInfo.find((s) => s.slug === kebabSlug)
		: null

	// Get smart search status from batch query (polled in parent layout)
	const { data: smartSearchStatusBatch } = useQuery(
		trpc.iconSets.getSmartSearchStatusBatchPublic.queryOptions(),
	)

	// Filter out processing icon sets for "all icons" view using polled status
	const readyIconSets = iconSetsInfo.filter(
		(s) => smartSearchStatusBatch?.find((b) => b.id === s.id)?.ready,
	)

	const iconSetSlugs = readyIconSets
		.map((iconSet) => iconSet.slug)
		.sort((a, b) => {
			if (a === 'untitled-ui-icons' && b !== 'untitled-ui-icons') return -1
			if (a !== 'untitled-ui-icons' && b === 'untitled-ui-icons') return 1
			return a.localeCompare(b)
		})

	const iconSetsToFetch = kebabSlug ? [kebabSlug] : iconSetSlugs

	const iconSetQueriesResults = useSerialQueries({
		queries: iconSetsToFetch.map((slug) =>
			trpc.iconSets.getIconSetBySlug.queryOptions({ slug }),
		),
		serialKey: 'iconSets',
	})

	const isPending = iconSetQueriesResults.every((r) => r.isPending)
	const hasMoreIconSets = iconSetQueriesResults.some((r) => r.isPending)

	const {
		query,
		setQuery,
		deferredQuery,
		debouncedQuery,
		searchType,
		setSearchType,
		grouping,
		setGrouping,
		iconName,
		format,
	} = useIconSetSearch({
		iconSetIdentifier: kebabSlug,
		isAnonymous: !isLoggedIn,
	})

	const directoriesWithIcons = useMemo(() => {
		return iconSetQueriesResults
			.map((result) => result.data)
			.filter((data) => data !== undefined)
			.flatMap((data) => {
				// Get subpath after iconSetSlug (e.g., "heroicons/regular/outline" -> "regular/outline")
				const subPath = kebabPath?.startsWith(data.slug + '/')
					? kebabPath.slice(data.slug.length + 1)
					: null

				const filteredIcons = filterIconsBySubPath(data.icons, subPath)

				return iconsToDirectories(
					filteredIcons.map((icon) => ({
						...icon,
						color: null,
						strokeWidth: null,
					})),
					data.name,
					data.slug,
					grouping === 'grouped',
				)
			})
			.sort((a, b) => {
				if (
					a.path.startsWith('untitled-ui-icons') &&
					!b.path.startsWith('untitled-ui-icons')
				)
					return -1
				if (
					!a.path.startsWith('untitled-ui-icons') &&
					b.path.startsWith('untitled-ui-icons')
				)
					return 1
				return a.path.localeCompare(b.path)
			})
	}, [iconSetQueriesResults, grouping, kebabPath])

	const isSmartSearchReady = kebabSlug
		? !!smartSearchStatusBatch?.find((s) => s.id === iconSetInfoForPath?.id)
				?.ready
		: (smartSearchStatusBatch?.some((s) => s.ready) ?? false)

	// Smart search - sequential per icon set (min 3 chars)
	const debouncedTrimmedQuery = debouncedQuery?.trim() ?? ''
	const canRunSmart =
		searchType === 'smart' &&
		isSmartSearchReady &&
		debouncedTrimmedQuery.length >= 3

	// Free users get 10 smart searches
	const {
		remaining: freeSearchesRemaining,
		hasSearchesLeft,
		isExceeded: isFreeSearchExceeded,
		isHydrated: isFreeSearchHydrated,
		decrementSearch,
		resetTime,
	} = useFreeSmartSearchLimit(debouncedTrimmedQuery, canRunSmart && !isLoggedIn)

	// Block free users when exhausted
	const shouldRunSmart = isLoggedIn
		? canRunSmart
		: canRunSmart && hasSearchesLeft

	const { smartResults, hasMoreSmartResults } = useIconSetSmartSearch({
		identifiers: iconSetsToFetch,
		query: debouncedTrimmedQuery,
		shouldRunSmart,
		endpoint: 'smartPublic',
	})

	// Decrement free search count when results arrive
	useEffect(() => {
		if (smartResults && Object.keys(smartResults).length > 0) {
			decrementSearch(debouncedTrimmedQuery)
		}
	}, [smartResults, debouncedTrimmedQuery, decrementSearch])

	const siteName = rootData?.domainConfig.siteName

	const iconSet = kebabSlug
		? iconSetsInfo.find((iconSet) => iconSet.slug === kebabSlug)
		: null

	const iconSetCount =
		iconSet?.count ??
		iconSetsInfo.reduce((count, iconSet) => count + iconSet.count, 0)

	const formattedCount = formatCount(iconSetCount)
	const placeholder =
		`Search ${formattedCount} icons` + String.fromCharCode(8230)

	const ogTitle = slug
		? `${formattedCount} ${iconSet?.name} Copy Paste Icons - ${siteName}`
		: `${siteName} | 40,000+ of Copy Paste Free Icons`

	const ogDescription = slug
		? `Color and customize ${iconSet?.name} icons then copy paste them into your projects.`
		: 'Color and customize 40,000+ copy paste icons then copy paste them into your projects.'

	const canonicalUrl = rootData?.origin
		? kebabPath
			? `${rootData.origin}/${kebabPath}`
			: rootData.origin
		: undefined

	const ssrHasMoreIcons = getSsrHasMoreIcons(loaderData?.ssrDirectories)

	// Get public icon sets info for the grid (need full info including author/license)
	const publicIconSetsInfo = iconSetsInfo.map((s) => ({
		name: s.name,
		slug: s.slug,
		author: s.author,
		authorUrl: s.authorUrl,
		license: s.license,
	}))

	return (
		<IconSetPage
			title={iconSet?.name ?? 'All Free Icon Sets'}
			placeholder={placeholder}
			query={query}
			deferredQuery={deferredQuery}
			onQueryChange={setQuery}
			searchType={searchType}
			onSearchTypeChange={setSearchType}
			isSmartSearchReady={isSmartSearchReady}
			smartSearchProcessing={!isSmartSearchReady}
			format={format}
			grouping={grouping}
			onGroupingChange={setGrouping}
			iconName={iconName}
			isLoggedIn={isLoggedIn}
			freeSearchesRemaining={freeSearchesRemaining}
			isFreeSearchExceeded={isFreeSearchExceeded}
			isFreeSearchHydrated={isFreeSearchHydrated}
			resetTime={resetTime}
			isPending={isPending}
			directoriesWithIcons={directoriesWithIcons}
			hasMoreIcons={hasMoreIconSets}
			ssrDirectories={loaderData?.ssrDirectories ?? undefined}
			ssrHasMoreIcons={ssrHasMoreIcons}
			publicIconSetsInfo={publicIconSetsInfo}
			smartResults={searchType === 'smart' ? smartResults : undefined}
			hasMoreSmartResults={hasMoreSmartResults}
			ogTitle={ogTitle}
			ogDescription={ogDescription}
			domainConfig={rootData?.domainConfig}
			canonicalUrl={canonicalUrl}
		/>
	)
}

export { IconSetErrorBoundary as ErrorBoundary }
